// Update your PaymentSettings component to remove deposit_type usage

import React from "react";
import { CreditCardIcon } from "@heroicons/react/24/outline";

const PaymentSettings = ({ settings, onInputChange }) => {
  const availablePaymentMethods = [
    { id: "cash", label: "Cash", icon: "💵" },
    { id: "check", label: "Check", icon: "📝" },
    { id: "card", label: "Credit/Debit Card", icon: "💳" },
    { id: "zelle", label: "Zelle", icon: "📱" },
    { id: "venmo", label: "Venmo", icon: "💸" },
    { id: "wire", label: "Wire Transfer", icon: "🏦" },
    { id: "paypal", label: "PayPal", icon: "🌐" },
    { id: "financing", label: "Financing Available", icon: "📊" },
  ];

  const handlePaymentMethodToggle = (methodId) => {
    const currentMethods = settings.payment_methods || [];
    const newMethods = currentMethods.includes(methodId)
      ? currentMethods.filter((m) => m !== methodId)
      : [...currentMethods, methodId];

    onInputChange("payment_methods", newMethods);
  };

  // Determine current deposit type based on existing data
  const getCurrentDepositType = () => {
    if (!settings.deposit_required) return "none";
    if (settings.deposit_amount) return "fixed";
    if (settings.deposit_percentage) return "percentage";
    return "none";
  };

  const currentDepositType = getCurrentDepositType();

  const handleDepositTypeChange = (type) => {
    if (type === "none") {
      onInputChange("deposit_required", false);
      onInputChange("deposit_amount", "");
      onInputChange("deposit_percentage", null);
    } else if (type === "fixed") {
      onInputChange("deposit_required", true);
      onInputChange("deposit_percentage", null); // Clear percentage when switching to fixed
    } else if (type === "percentage") {
      onInputChange("deposit_required", true);
      onInputChange("deposit_amount", ""); // Clear amount when switching to percentage
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          💳 Payment & Pricing Settings
        </h3>
        <p className="text-gray-600 mb-6">
          Configure payment methods, service pricing, and deposit requirements. Your AI will
          reference these when discussing pricing with customers.
        </p>

        <div className="space-y-8">
          {/* Service Pricing Section - Simple Text Field */}
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
              <div>
                <h4 className="font-medium text-orange-900">
                  💰 Service Pricing Information
                </h4>
                <p className="text-sm text-orange-700">
                  Enter your pricing information that your AI will reference when customers ask about costs
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-orange-800 mb-2">
                  Pricing Information
                </label>
                <textarea
                  value={settings.pricing_info || ""}
                  onChange={(e) => onInputChange("pricing_info", e.target.value)}
                  rows={4}
                  placeholder="Example: Service calls start at $175, Emergency calls $275, Free estimates on installations, Oil changes $35-65, Brake pads $150-300"
                  className="w-full border border-orange-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="text-xs text-orange-600 mt-1">
                  Enter your services and pricing in a natural format. Your AI will reference this information when customers ask about costs.
                </p>
              </div>

              {/* Example Format */}
              <div className="bg-white border border-orange-200 rounded-lg p-4">
                <h5 className="font-medium text-orange-900 mb-2">💡 Example Format:</h5>
                <div className="text-sm text-orange-800 space-y-1">
                  <p className="italic">"Service calls start at $175, Emergency calls $275, Free estimates on installations"</p>
                  <p className="italic">"Oil changes $35-65, Brake repairs $150-300, Tire rotation $25"</p>
                  <p className="italic">"Plumbing service calls $125, Emergency plumbing $200, Free quotes"</p>
                </div>
              </div>

              {/* Current Pricing Preview */}
              {settings.pricing_info && (
                <div className="bg-white border border-orange-200 rounded-lg p-4">
                  <h5 className="font-medium text-orange-900 mb-2">
                    ✅ Your AI will reference this pricing:
                  </h5>
                  <p className="text-sm text-orange-800 italic">
                    "{settings.pricing_info}"
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Methods Section */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CreditCardIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-green-900">
                  💳 Accepted Payment Methods
                </h4>
                <p className="text-sm text-green-700">
                  Select all payment methods you accept
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {availablePaymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    settings.payment_methods?.includes(method.id)
                      ? "border-green-500 bg-green-100"
                      : "border-gray-200 bg-white hover:border-green-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={
                      settings.payment_methods?.includes(method.id) || false
                    }
                    onChange={() => handlePaymentMethodToggle(method.id)}
                    className="sr-only"
                  />
                  <span className="text-2xl">{method.icon}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {method.label}
                  </span>
                </label>
              ))}
            </div>

            {settings.payment_methods?.length > 0 && (
              <div className="mt-4 p-4 bg-white border border-green-200 rounded-lg">
                <h5 className="font-medium text-green-900 mb-2">
                  ✅ Your AI will say:
                </h5>
                <p className="text-sm text-green-800 italic">
                  "We accept {settings.payment_methods.join(", ")}
                  {settings.payment_methods.length > 1
                    ? " — whatever's easiest for you"
                    : ""}
                  ."
                </p>
              </div>
            )}
          </div>

          {/* Deposit Requirements Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
              <div>
                <h4 className="font-medium text-blue-900">
                  💰 Deposit Requirements
                </h4>
                <p className="text-sm text-blue-700">
                  Set deposit policies for your services
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Deposit Type Selection */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-3">
                  Deposit Policy
                </label>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => handleDepositTypeChange("none")}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg border-2 transition-all text-left ${
                      currentDepositType === "none"
                        ? "border-blue-500 bg-blue-50 text-blue-900"
                        : "border-gray-200 bg-white text-gray-700 hover:border-blue-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        currentDepositType === "none"
                          ? "border-blue-500"
                          : "border-gray-300"
                      }`}
                    >
                      {currentDepositType === "none" && (
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      No deposit required
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDepositTypeChange("fixed")}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg border-2 transition-all text-left ${
                      currentDepositType === "fixed"
                        ? "border-blue-500 bg-blue-50 text-blue-900"
                        : "border-gray-200 bg-white text-gray-700 hover:border-blue-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        currentDepositType === "fixed"
                          ? "border-blue-500"
                          : "border-gray-300"
                      }`}
                    >
                      {currentDepositType === "fixed" && (
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      Fixed deposit amount
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDepositTypeChange("percentage")}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg border-2 transition-all text-left ${
                      currentDepositType === "percentage"
                        ? "border-blue-500 bg-blue-50 text-blue-900"
                        : "border-gray-200 bg-white text-gray-700 hover:border-blue-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        currentDepositType === "percentage"
                          ? "border-blue-500"
                          : "border-gray-300"
                      }`}
                    >
                      {currentDepositType === "percentage" && (
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      Percentage of total
                    </span>
                  </button>
                </div>
              </div>

              {/* Fixed Amount Input */}
              {currentDepositType === "fixed" && (
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    Fixed Deposit Amount
                  </label>
                  <input
                    type="text"
                    value={settings.deposit_amount || ""}
                    onChange={(e) =>
                      onInputChange("deposit_amount", e.target.value)
                    }
                    placeholder="$500"
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Percentage Input */}
              {currentDepositType === "percentage" && (
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    Deposit Percentage
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={settings.deposit_percentage || ""}
                      onChange={(e) =>
                        onInputChange(
                          "deposit_percentage",
                          parseInt(e.target.value) || null
                        )
                      }
                      min="1"
                      max="100"
                      placeholder="50"
                      className="w-20 border border-blue-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-blue-800">%</span>
                  </div>
                </div>
              )}

              {/* Threshold Amount */}
              {currentDepositType !== "none" && (
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    Minimum Job Amount for Deposit (Optional)
                  </label>
                  <input
                    type="number"
                    value={settings.deposit_threshold_amount || ""}
                    onChange={(e) =>
                      onInputChange(
                        "deposit_threshold_amount",
                        parseFloat(e.target.value) || null
                      )
                    }
                    placeholder="500"
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    Only jobs above this amount require a deposit. Leave blank
                    if all jobs require deposit.
                  </p>
                </div>
              )}

              {/* Custom Deposit Policy */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Custom Deposit Policy (Optional)
                </label>
                <textarea
                  value={settings.deposit_policy || ""}
                  onChange={(e) =>
                    onInputChange("deposit_policy", e.target.value)
                  }
                  rows={3}
                  placeholder="Additional deposit terms, refund policy, etc."
                  className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-blue-600 mt-1">
                  Any additional details about your deposit policy that your
                  AI should know.
                </p>
              </div>

              {/* Preview */}
              {currentDepositType !== "none" && (
                <div className="mt-4 p-4 bg-white border border-blue-200 rounded-lg">
                  <h5 className="font-medium text-blue-900 mb-2">
                    ✅ Your AI will say:
                  </h5>
                  <p className="text-sm text-blue-800 italic">
                    {currentDepositType === "fixed" &&
                      settings.deposit_amount && (
                        <>
                          "We require a {settings.deposit_amount} deposit to
                          schedule
                          {settings.deposit_threshold_amount
                            ? ` for jobs over $${settings.deposit_threshold_amount}`
                            : ""}
                          ."
                        </>
                      )}
                    {currentDepositType === "percentage" &&
                      settings.deposit_percentage && (
                        <>
                          "We require a {settings.deposit_percentage}% deposit
                          to schedule
                          {settings.deposit_threshold_amount
                            ? ` for jobs over $${settings.deposit_threshold_amount}`
                            : ""}
                          ."
                        </>
                      )}
                  </p>
                  {settings.deposit_policy && (
                    <p className="text-xs text-blue-700 mt-2">
                      Additional policy: {settings.deposit_policy}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* How AI Uses This Info */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
            <h4 className="font-medium text-purple-900 mb-3">
              🤖 How Your AI Uses Payment & Pricing Info
            </h4>
            <div className="space-y-3 text-sm text-purple-800">
              <div className="flex items-start space-x-2">
                <span className="text-purple-600">•</span>
                <p>
                  <strong>Service Pricing:</strong> AI references your pricing information when customers ask about costs
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-purple-600">•</span>
                <p>
                  <strong>Payment Methods:</strong> "We accept{" "}
                  {settings.payment_methods?.join(", ") ||
                    "various payment methods"}
                  "
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-purple-600">•</span>
                <p>
                  <strong>Scheduling:</strong> AI mentions deposit requirements when booking appointments
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-purple-600">•</span>
                <p>
                  <strong>Professional Communication:</strong> AI explains all policies clearly and professionally
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default React.memo(PaymentSettings);